import { ComponentProps, FC } from 'react';
import { Box, Stack } from '@mui/material';
import {
  PersonOutlineOutlined,
  VisibilityOutlined,
  type SvgIconComponent as MuiSvgIconComponent,
} from '@mui/icons-material';
import { ActionsMenu, Chip, Link, Typography } from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { DepartmentManager } from 'store/departments';

interface Props {
  name: string;
  id: string;
  hidden: boolean;
  manager: DepartmentManager | null;
  icon: MuiSvgIconComponent;
  actions?: ComponentProps<typeof ActionsMenu>['items'];
  nested?: boolean;
}

export const OrgStructureRow: FC<Props> = ({
  name,
  id,
  hidden,
  manager,
  icon: Icon,
  actions,
  nested,
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      py={1}
      px={2}
      ml={nested ? 4 : 0}
      borderRadius={1}
      boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
    >
      <Box flexShrink={0}>
        <Icon sx={{ fontSize: 20, color: SystemColors.grey[55] }} />
      </Box>

      <Stack gap={0.25} alignItems="flex-start" minWidth={0} flex={1}>
        <Typography variant="h3">{name}</Typography>
        <Typography color="textSecondary">
          {t('marketplace.employeeID', { id })}
        </Typography>

        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
          {hidden ? null : (
            <Chip
              label={t('departments.visible.label')}
              variant="neutral"
              icon={VisibilityOutlined}
            />
          )}

          {manager ? (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <PersonOutlineOutlined
                sx={{ fontSize: 16, color: SystemColors.grey[55] }}
              />
              <Typography color="textSecondary">
                {t('companies.manager.label')}:
              </Typography>
              <Link
                label={manager.name}
                to={ROUTES.employees.details.replace(':id', manager.id)}
              />
            </Stack>
          ) : null}
        </Stack>
      </Stack>

      {actions?.length ? <ActionsMenu type="spread" items={actions} /> : null}
    </Stack>
  );
};
